/**
 * useFeatureTracking — Feature Discovery System
 *
 * Tracks which features the user visits by mapping the current route
 * to a feature key and firing a POST to /api/feature-visits/track.
 *
 * - Fires on route change via useLocation()
 * - Debounced: tracks each feature at most once per session (in-memory Set)
 * - Completely silent: never shows errors, never blocks UI
 *
 * Add this hook to the main Layout component so it runs globally.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import api from '../services/api/client';

/** Maps pathnames to feature keys for tracking */
const ROUTE_FEATURE_MAP: Record<string, string> = {
  '/command/tasks': 'tasks',
  '/command': 'tasks',
  '/command/protocols': 'protocols',
  '/command/training': 'training',
  '/command/contracts': 'contracts',
  '/command/chastity': 'chastity',
  '/command/points': 'points',
  '/command/praise': 'praise',
  '/command/rewards': 'rewards',
  '/command/autonomy': 'autonomy',
  '/chronicle/journal': 'journal',
  '/chronicle/check-in': 'check-in',
  '/chronicle/scenes': 'scenes',
  '/chronicle/boundaries': 'boundaries',
  '/chronicle/safety': 'safety',
  '/chronicle/timeline': 'timeline',
  '/chronicle/collaring': 'collaring',
  '/chronicle/inventory': 'inventory',
  '/chronicle/body-map': 'body-map',
  '/cultivate': 'discovery-feed',
  '/cultivate/matches': 'discovery-matches',
  '/connect/communities': 'communities',
  '/connect/dms': 'dms',
};

/**
 * Resolve a pathname to a feature key.
 * Tries exact match first, then prefix match (longest prefix wins).
 */
function resolveFeatureKey(pathname: string): string | null {
  // Exact match
  if (ROUTE_FEATURE_MAP[pathname]) {
    return ROUTE_FEATURE_MAP[pathname];
  }

  // Prefix match — find the longest matching prefix
  let bestMatch: string | null = null;
  let bestLength = 0;

  for (const route of Object.keys(ROUTE_FEATURE_MAP)) {
    if (pathname.startsWith(route + '/') && route.length > bestLength) {
      bestMatch = ROUTE_FEATURE_MAP[route];
      bestLength = route.length;
    }
  }

  return bestMatch;
}

/**
 * Hook that tracks feature visits globally.
 * Call once in the Layout component — it watches route changes automatically.
 */
export function useFeatureTracking(): void {
  const { pathname } = useLocation();
  const trackedThisSession = useRef<Set<string>>(new Set());

  const trackMutation = useMutation({
    mutationFn: (featureKey: string) => api.post('/feature-visits/track', { featureKey }),
    // Pre-migration was fire-and-forget with a swallowed .catch() and no
    // retry wrapper — tracking should never affect user experience.
    retry: false,
  });

  useEffect(() => {
    const featureKey = resolveFeatureKey(pathname);
    if (!featureKey) return;

    // Already tracked this session — skip
    if (trackedThisSession.current.has(featureKey)) return;

    // Mark as tracked immediately (before async call)
    trackedThisSession.current.add(featureKey);

    // Fire and forget — mutate() (not mutateAsync) never awaited, errors
    // are swallowed by useMutation's own unhandled-rejection-safe internals.
    trackMutation.mutate(featureKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);
}
